import type { LineageSku, SkuProductTaxonomy } from '../api/client.js';
import { categoryLabel, gaugeLabel, productsPath, putUpWithLength } from './products.js';

/**
 * The product page's pure pieces: the breadcrumb back into the Products browser,
 * the variant facts (color and size, or a string's gauge and put-up), the notes to
 * show (notes are per SKU; the live SKU's lead), the SKU the per-SKU tabs open on,
 * and the lineage summary line. See docs/products.md, "The product page".
 */

export interface Crumb {
  label: string;
  to: string;
}

/**
 * Products › category › series › model, each linking into the browser at that
 * level (the drill keys carry the no-series / no-model sentinels for a null node).
 */
export function productBreadcrumb(t: SkuProductTaxonomy): Crumb[] {
  const { category, series, model } = t.drill;
  return [
    { label: 'Products', to: '/products' },
    { label: categoryLabel(category), to: productsPath({ category }) },
    { label: t.series ?? '(no series)', to: productsPath({ category, series }) },
    { label: t.model ?? '(no model)', to: productsPath({ category, series, model }) },
  ];
}

/** The variant facts under the title: color, then size — or a string's gauge and put-up. */
export function variantFacts(t: SkuProductTaxonomy): { label: string; value: string }[] {
  const facts = [{ label: 'Color', value: t.color ?? '—' }];
  if (t.category === 'string') {
    facts.push({ label: 'Gauge', value: gaugeLabel(t.gauge, t.gaugeMm) });
    facts.push({ label: 'Put-up', value: putUpWithLength(t.putUp, t.putUpLength) });
  } else {
    facts.push({ label: 'Size', value: t.sizeLabel ?? '—' });
  }
  return facts;
}

/** The SKU whose notes lead, and the other SKUs' notes worth showing. */
export interface ProductNotes {
  /** The live SKU (or, on a retired product, the newest listing); null with no SKUs. */
  lead: LineageSku | null;
  /** Why the lead SKU leads, for its label. */
  leadReason: 'live' | 'newest' | null;
  /** Every other SKU that carries a note, oldest first seen first. */
  others: LineageSku[];
}

/**
 * Notes are per SKU today (a product notes table is a later phase), so the page
 * shows the live SKU's notes, labeled, and lists any note left on another SKU of
 * the product beneath it so none is hidden. A retired product (no live SKU) leads
 * with its newest listing.
 */
export function productNotes(lineage: readonly LineageSku[]): ProductNotes {
  const live = lineage.find((s) => s.live) ?? null;
  const newest =
    [...lineage].sort((a, b) => b.firstSeenDate.localeCompare(a.firstSeenDate) || b.id - a.id)[0] ??
    null;
  const lead = live ?? newest;
  return {
    lead,
    leadReason: lead === null ? null : live ? 'live' : 'newest',
    others: lineage.filter((s) => s !== lead && (s.notes ?? '').trim() !== ''),
  };
}

/** The SKU the per-SKU sell-through tabs open on: the live one, else the first listed. */
export function defaultSkuTab(
  skus: readonly { skuId: number }[],
  liveSkuId: number | null,
): number | null {
  if (liveSkuId !== null && skus.some((s) => s.skuId === liveSkuId)) return liveSkuId;
  return skus[0]?.skuId ?? null;
}

/** "2 seller SKUs · 1 Active · 1 no longer listed": the lineage at a glance. */
export function lineageSummary(lineage: readonly LineageSku[]): string {
  const parts = [`${lineage.length} seller SKU${lineage.length === 1 ? '' : 's'}`];
  const counts = new Map<string, number>();
  for (const s of lineage) {
    const status = s.status ?? 'No status';
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  for (const [status, n] of [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))) {
    parts.push(`${n} ${status === 'Active' ? status : status.toLowerCase()}`);
  }
  return parts.join(' · ');
}
